import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { Observable, delay, filter, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreBaseApiCommandParams } from '@app/core/models/core-base-api.model';
import { CoreModuleName, LocalStorageKeys } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse, IDynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationApiService, IReservationApiServiceOption, ReservationServiceData, ReservationServiceOption } from '@app/res-common/models/rescom-service.model';
import { IResVillaAvailabilityParams, IReservationModuleAPIConfiguration, ResVillaAvailability } from '@app/res-common/models/rescom.model';
import { DateTime } from 'luxon';
import { ReservationApiBoolean } from '../../res-common/models/res-constant.model';
import { ReservationActionBooking } from '../../res-common/models/reservation-permission.model';
import { BookingGuestAirData, IResApiFutureFlights, IResApiGetAirFlightParams, IResApiManualFlights, IReservationApiAirPnr, IReservationApiChangePnrAirClass, IReservationApiDeleteAirPnrParams, IReservationApiImportPNRParams, IReservationApiRefreshAirResultPnrsParams, IReservationApiUpdateBookingGuestAirData, ResAirFutureFlight, ResAirInternalFlight, ResRightRezOfficeIdConfig, ReservationAirPnr } from '../models/res-air.model';
import { IResApiCreateBookingCertificate, IResApiGetBookingCertificateDetail, IResApiImportCertificate, IResApiSearchCertificate, ResBookingCertificate, ResBookingCertificateAvailableAmount, ResBookingCertificateDetail, ResCertificateType } from '../models/res-bkg-certificate.model';
import { IReservationApiCreateBkgDocumentParams } from '../models/res-bkg-document.model';
import { IResApiBkgItemsManage } from '../models/res-bkg-item.model';
import { IReservationApiGetBkgNotes, IReservationApiManageBkgNotes, ResBkgNote, ResBkgNoteTouch } from '../models/res-bkg-notes.model';
import { BookingCheckSaleRequest, ResDebugBkgItinerary, BookingSaleRequest, BookingToRequest, IResApiGetBkgDiscountAnalysysParams, IResApiGetBkgJourneyItineraryHistoryDetailsParams, IResApiGetBkgJourneyItineraryHistoryParams, IResApiGetBkgJourneyItineraryParams, IResApiGetBkgSuggestedItems, IResApiGetBkgTopicHistoryDetailsParams, IResApiGetDayByDayAllImagesByCategoryParams, IResApiGetDayByDayImagesParams, IResApiGetExportInvoiceDataParams, IResApiGetPromoDMCParams, IResApiIntraBookingImportParams, IResApiLinkedBookingManageParams, IResApiManageBkgJourneyItinerary, IResApiManageDayByDayImagesParams, IResApiManageJetSeats, IResApiParametersQuoteCreate, IResApiSyncIntercompanyBookingParams, IResBookingCreateApiResponse, IResBookingGroup, IResBookingManageConfirmSuiteStatusParams, IReservationApiBookingActionCheckParameters, IReservationApiBookingActionParameters, IReservationApiBookingAddPackageParameters, IReservationApiBookingDelete, IReservationApiBookingExternalGifUrlParameters, IReservationApiBookingRequestParameters, IReservationApiBookingVersionRequestParameters, IReservationApiEditBookingParameters, IReservationApiManageBkgWishlistParams, Market, ResAccountingExportData, ResApiBookingActionResponse, ResBkgCommunicationEmail, ResBkgCommunicationEmailTemplate, ResBkgDiscountAnalysys, ResBkgDiscountAnalysysGroup, ResBkgDmc, ResBkgExtraItem, ResBkgIntercompanyList, ResBkgJourneyItinerary, ResBkgJourneyItineraryHistory, ResBkgJourneyItineraryHistoryDates, ResBkgLinks, ResBkgMainDestination, ResBkgPromoDMC, ResBkgSalesAssistant, ResBkgSecondarySalesAgent, ResBkgSecondaryTravelAgency, ResBkgSegment, ResBkgSubSegment, ResBkgSuggestedItem, ResBkgTemplateTag, ResBkgTopicHistoryDates, ResBkgTopicHistoryDetail, ResBkgVersionHistory, ResBkgWishlist, ResBooking, ResBookingCancelReason, ResBookingDayByDayAllImages, ResBookingDayByDayImages, ResBookingFinancialRecap, ResBookingLanguage, ResBookingMultiVoyage, ResBookingV2VParams, ResFccCalculation, ResFinancialSplit, ResFinancialSplitMultiCurrency, ResPaymentMilestone, IResManagePaymentMilestoneParams, ReservationBookingMarginality, ResBkgExperienceAdditional, ResBkgIncludedItems, IResApiManageBkgIncludedItemsParams, ResBkgAllowanceItems, ResMiniPackageBkgList, IResApiResetDayByDayImagesParams, ResDMCDocCheck, IResPayerItemsManageParams, IReservationApiDMCReference, IReservationApiManageDMCReferenceParams, ResEresIntegrationCheck, BookingJourneySupplement, IResApiReinstateItem, createJourneySupplementFromReinstateItem, IResApiSaveRoomConfigRoom } from '../models/res-booking.model';
import { IReservationLoyaltyDiscount, IReservationNoteType, IReservationPriceType } from '../models/res-cached-data.models';
import { IReservationApiCreatePurchaseOrderParams, IReservationApiCreatePurchaseOrderResult, IReservationApiGetInvoicePackagesParams, IReservationApiGetMultiBookingParams, ResMultiBookingInvoicePackage, ResMultiBookingItem } from '../models/res-multibooking-invoice.model';
import { ResDeck } from '../models/res-cruise-availability.models';
import { IReservationApiDeleteCruiseParams, IReservationApiUpdateSuiteData, ResSuitePriceCategory } from '../models/res-cruise.models';
import { ResDepositToBookingConversionParams } from '../models/res-deposit.model';
import { IReservationCheckComboAvailabilityParams, IReservationDeleteOptionPackageParams, IReservationEditServiceOptionParams, IReservationGetBkgOptionsParams, IReservationGetBkgServicesParams, IReservationSuiteType, IReservationUpdateMessageParams, IResManageMultipleEditItem, IReservationAddExtraOptionParams, IReservationAddGuestToItemParams, IServiceOptionGuest2Room, IResBkgItemChangePriceType } from '../models/res-option.models';
import { IResApiGuestCheckBookingExistsParams, IResApiGuestCheckBookingExistsResult } from '../models/res-owner.model';
import { BookingCreatedBy, IResApiBkgSaveAsTemplate, IResApiChangeBookingOwnerParams, IResApiGetDayByDayPackagesParams, IResApiGetDayByDayTopicItineraryParams, IResApiManageDayByDayInclusionsParams, IResApiManageDayByDayNotesItinerary, IResApiManageDayByDayPackagesParams, IResApiManageDayByDayTopicItinerary, IResApiManageSupplierItemRequestListItem, IResApiSupplierEditRequest, IReservationApiDeletePackageParams, ResBookingDayByDayNotesSuggestion, ResBookingInclusionsDayByDay, ResBookingNotesDayByDay, ResBookingPackageDayByDay, ResBookingTopicDayByDay, ResSupplierEditRequestDetails } from '../models/res-package.model';
import { IResApiManageBookingPayerManagementParams, ResBookingPayerDetails, ResBookingPayerMngmtGroups, ResBookingPayerMngmtGuests, ResBookingPayerMngmtPayers, ResBookingPayerMngmtRelations, ResBookingPayerMngmtRoles, ResMaganeBookingPayerResponse } from '../models/res-payment.model';
import { ResExperienceOptional } from '@app/res-common/models/res-experience.model';

import { IReservationApiTsUpserGuest, IResRoomAvailabilityCheckParams } from './reservation-api.interfaces';
@Injectable({
  providedIn: 'root'
})
export class ReservationBookingApiService extends EidosBaseApiService {
  isUatEnv = false;

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.isUatEnv = this.eidosSecurityService.isOnByLevel('uat');
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  public addExtraOptions(params: IReservationAddExtraOptionParams) {
    return this.postDynamicAPI('extraOption/add', params);
  }

  addGuestToItem(params:IReservationAddGuestToItemParams) {
    return this.postDynamicAPI('bkgitem/addguest', params);
  }

  bkgItemChangePriceType(params:IResBkgItemChangePriceType): Observable<any> {
    return this.postDynamicAPI('bkgOption/changePriceType', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    )
  }

  bkgSaveAsTemplate(params: IResApiBkgSaveAsTemplate): Observable<any> {
    return this.postDynamicAPI('bkgtemplate/save', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * Request autheticated url to open myactivities on web site
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  bookingAddPackage(params: IReservationApiBookingAddPackageParameters) {
    return this.callDynamicAPI('booking/addpackage', params, 'POST').pipe(
      map<IDynamicApiResponse, IDynamicApiResponse>(res => {
        return res;
      })
    );
  }

  /**
   * Perform booking V2V
   *
   * @param {ResBookingV2VParams} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  bookingV2V(params: ResBookingV2VParams) {
    return this.postDynamicAPI('booking/v2v', params);
  }

  changeBookingOwner(params: IResApiChangeBookingOwnerParams) {
    return this.postDynamicAPI('booking/changeOwner', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public changeODFare(params: { BkgID: number, NEWPriceTypeID: number }): Observable<any> {
    return this.postDynamicAPI('openDeposit/changeFare', params);
  }

  changePnrAirClass(params: IReservationApiChangePnrAirClass): Observable<any> {
    return this.postDynamicAPI('pnrAirClass/update', params);
  }

  /**
   * Checks is if can be linked to bkgidprv as IsSameVyg
   *
   * @param {{ BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean }} params
   * @return {*}  {(Observable<(any[] | {})>)}
   * @memberof ReservationBookingApiService
   */
  checkBkgLink(params: { BkgID: number, BkgIDPrv: number, IsSameVyg: ReservationApiBoolean, UniqueExperience?: string }): Observable<(any[] | {})> {
    return this.callDynamicAPI('booking/checkLink', params, 'GET').pipe(
      map<DynamicApiResponse, any[] | {}>((response) => response.body)
    );
  }

  /**
   * Check if can perform booking action
   *
   * @param {IReservationApiBookingActionCheckParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  checkBookingAction(params: IReservationApiBookingActionCheckParameters) {

    const { Action, ...apiParams } = params;
    let apiAction = '';

    switch (Action) {
      case ReservationActionBooking.ChangeVoyageBookingVersion:
        apiAction = 'ChangeVoyage';
        break;
      default:
        throw {
          name: 'Unhandled booking action',
          message: 'Not implemented a check handling for this booking action.'
        };
    }

    return this.postDynamicAPI('booking/checkAction', {
      Action: apiAction,
      ...apiParams
    });
  }

  checkBookingDayByDay(BkgID: number) {
    return this.getDynamicAPI('booking/getDayByDayCheck', { BkgID }).pipe(
      map<DynamicApiResponse, boolean>((response) => {
        return response.body?.[0]?.HasDayByDay == 'Y';
      })
    );
  }

  checkBookingDayByDayForProposal(BkgID: number): Observable<{ hasDayByDay: boolean, hasPersonalization: boolean, hasGeneralInclusions: boolean }> {
    return this.getDynamicAPI('booking/getDayByDayCheck', { BkgID }).pipe(
      map<DynamicApiResponse, { hasDayByDay: boolean, hasPersonalization: boolean, hasGeneralInclusions: boolean }>((response) => {
        const fritsRow = response.body?.[0] ?? {}
        return {
          hasDayByDay: fritsRow.HasDayByDay === 'Y',
          hasPersonalization: fritsRow.HasPersonalization === 'Y',
          hasGeneralInclusions: fritsRow.HasGeneralInclusions === 'Y'
        };
      })
    );
  }

  public checkBookingSaleRequests(bkgId: number): Observable<BookingCheckSaleRequest[]> {
    return this.callDynamicAPI('booking/checkSaleRequest', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, BookingCheckSaleRequest[]>((response) => {
        return response.body.map((data: any) => new BookingCheckSaleRequest(data));
      })
    )
  }

  public checkComboAvailability(params: IReservationCheckComboAvailabilityParams) {
    return this.postDynamicAPI('serviceOption/checkComboAvailability', params).pipe(
      map<DynamicApiResponse, IReservationSuiteType[]>((response) => response.body.map((p: IReservationSuiteType) => p))
    );
  }

  checkRoomAvailability(_params: IResRoomAvailabilityCheckParams): Observable<boolean> {
    return of(true).pipe(delay(600));
  }

  /**
   * Perform booking version cloning
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  cloneBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.callDynamicAPI('booking/addversion', params, 'POST');
  }

  /**
   * Performs booking version confirmation
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  confirmBooking(params: IReservationApiBookingVersionRequestParameters) {
    return this.callDynamicAPI('booking/confirm', params, 'POST');
  }

  confirmVilla(bkgID: number) {
    return this.postDynamicAPI('booking/confirmVilla', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  /**
   * Convert an OD to a booking
   *
   * @param {ResDepositToBookingConversionParams} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  convertODToBkg(params: ResDepositToBookingConversionParams) {
    return this.postDynamicAPI('booking/voyage2voyage', params)
  }

  createBkgCertificate(params: IResApiCreateBookingCertificate): Observable<any> {
    return this.postDynamicAPI('bkgCertificate/create', params);
  }

  /**
   * Create booking with specified params
   *
   * @param {IResApiParametersQuoteCreate} params
   * @return {*}  {Observable<IResBookingCreateApiResponse>}
   * @memberof ReservationBookingApiService
   */
  createBooking(params: IResApiParametersQuoteCreate, multiBkg = false): Observable<IResBookingCreateApiResponse> {
    const url = multiBkg ? 'quote/createMultiBkg' : 'quote/create';
    return this.postDynamicAPI(url, params).pipe(
      map<DynamicApiResponse, IResBookingCreateApiResponse>((apiResponse) => {
        let response: IResBookingCreateApiResponse = {
          BkgID: ResBooking.INVALID_BOOKING
        };

        //TODO: check function Artiom
        //Exception only used in Heritage deposit page
        // if(apiResponse.body.DT0[0].BkgID) {
        //   response.BkgID = apiResponse.body.DT0[0].BkgID ?? ResBooking.INVALID_BOOKING;
        //   return response;
        // }

        if (!!apiResponse && Array.isArray(apiResponse.body) && apiResponse.body.length > 0) {
          response.BkgID = apiResponse.body[0].BkgID ?? apiResponse.body[0].MasterBkgID ?? ResBooking.INVALID_BOOKING;
          response.ErrorMessage = apiResponse.body[0].ErrorMessage;
        }

        return response;
      })
    );
  }

  createIntraBooking(bkgID: number) {
    return this.postDynamicAPI('booking/manageDMCBooking', { BkgIDTOC: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public createInvoiceForBkg(params: IReservationApiCreateBkgDocumentParams): Observable<any> {
    return this.postDynamicAPI('invoiceForBkg/create', params);
  }

  public createPurchaseOrder(params: IReservationApiCreatePurchaseOrderParams): Observable<IReservationApiCreatePurchaseOrderResult> {
    return this.postDynamicAPI('payableCycle/createPurchaseOrderMultiLine', params)
  }

  deleteAirPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {

    return (isTicketed ? this.postDynamicAPI('airPnr/delete', params) : this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnr',
      data: params,
      type: 'DELETE',
      customBaseUrl: this.pluginUrlAPI
    }))).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  deleteAirRRPnr(params: IReservationApiDeleteAirPnrParams, isTicketed: boolean): Observable<string> {
    params.button = 'Delete'
    return (isTicketed ? this.postDynamicAPI('airPnr/delete', params) : this.sendCommand(new CoreBaseApiCommandParams({
      url: 'oem/rightrez/Cancel',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    }))).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  /**
   * Perform booking version deletion
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  deleteBooking(params: IReservationApiBookingDelete) {
    return this.callDynamicAPI('booking/delete', params, 'POST');
  }

  deleteBookingPreCheck(bkgID?: number, section: 'CancelBooking' | 'ChangeVoyage' = 'CancelBooking'): Observable<{ MessageCod: string, MessageDesc: string }[]> {
    return this.getDynamicAPI('booking/getCheckBkgDelete', { BkgID: bkgID, Section: section }).pipe(
      map<DynamicApiResponse, { MessageCod: string, MessageDesc: string }[]>((response) => {
        const body = response.body
        return Array.isArray(body) ? body : []
      })
    );
  }

  public deleteOptionPackage(params: IReservationDeleteOptionPackageParams) {
    return this.postDynamicAPI('serviceOption/delete', params);
  }

  public deletePackage(params: IReservationApiDeletePackageParams): Observable<any> {
    return this.postDynamicAPI('package/delete', params);
  }

  public deleteVoyage(params: IReservationApiDeleteCruiseParams): Observable<any> {
    return this.postDynamicAPI('cruise/delete', params);
  }

  public downloadBlobFileInline(guid: string) {
    let headers = this.getHeaders(null);
    let token = localStorage.getItem(LocalStorageKeys.TOKEN);
    if (token) {
      headers = headers.set(LocalStorageKeys.TOKEN, token);
    }

    let options = {
      withCredentials: true,
      headers,
      observe: 'response' as 'body',
      responseType: 'blob' as 'blob'
    };

    let apiUrl = `${this.config?.urlAPI}/documents/document-blob?FileGuid=${guid}&FileInline=false`;
    return this.http.get(apiUrl, options as any);
  }

  /**
   * Post booking edit params to edit a booking
   *
   * @param {IReservationApiEditBookingParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  editBooking(params: IReservationApiEditBookingParameters) {
    return this.callDynamicAPI('booking/edit', params, 'POST');
  }

  public editServiceOption(params: IReservationEditServiceOptionParams) {
    return this.postDynamicAPI('serviceOption/update', params);
  }

  public execMarginCalculator(bkgID: number) {
    return this.callDynamicAPI('booking/marginCalculator', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, boolean>(response => !!response.body)
    );
  }

  /**
   * Performs a booking action
   *
   * @param {IReservationApiBookingActionParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  executeBookingAction(params: IReservationApiBookingActionParameters, options?: any) {
    let route: string = '';
    switch (params.Action) {
      case ReservationActionBooking.ChangeSuiteBookingVersion:
        route = 'booking/changesuite';
        break;
      case ReservationActionBooking.PromoteBooking:
        route = 'booking/promote';
        break;
    }

    const { Action, ...routeParams } = params;

    if (options?.keepPrice) routeParams.KeepPrice = 'Y'

    return this.callDynamicAPI(route, routeParams, 'POST').pipe(
      map<DynamicApiResponse, ResApiBookingActionResponse>((response) => {
        const res = new ResApiBookingActionResponse()
        if (response.body.length > 0) {
          if (response.body[0].ErrorCod) {
            res.error = true
            res.ok = false
            res.errorMessage = response.body.map((e: any) => `${e.ErrorMessage}`)
          }
        }
        return res;
      })
    );
  }

  getAirPnrs(bookingId: number): Observable<ReservationAirPnr[]> {
    return this.sendCommand(new CoreBaseApiCommandParams({
      url: 'gmt/pnr',
      data: { bookingId },
      type: 'GET',
      customBaseUrl: this.pluginUrlAPI
    })).pipe(
      map<IReservationApiAirPnr[], Array<ReservationAirPnr>>((response) => {
        return response.map((pnr: any) => new ReservationAirPnr(pnr));
      })
    );
  }

  getAllLinkedBkgs(params: { BkgID: number }): Observable<ResBkgLinks> {
    return this.getDynamicAPI('booking/checkBookingLink', params).pipe(
      map<DynamicApiResponse, ResBkgLinks>((response) => {
        return new ResBkgLinks(response.body)
      }))
  }

  public getAssignedTo(params: { EntityType?: string, Entity?: number, TouchId?: number }): Observable<Array<{ Cod: string, Desc: string }>> {
    return this.callDynamicAPI('touch/assignedto', params).pipe(
      map<DynamicApiResponse, Array<{ Cod: string, Desc: string }>>((response) => {
        return response.body;
      })
    );
  }

  getBkgAdditionalExp(bkgID: number, experienceID: number): Observable<ResBkgExperienceAdditional[]> {
    return this.callDynamicAPI('booking/getBkgAdditionalExp', { BkgID: bkgID, ExperienceID: experienceID }).pipe(
      map<DynamicApiResponse, ResBkgExperienceAdditional[]>((response) => response.body.map((data: any) => new ResBkgExperienceAdditional(data)))
    );
  }

  getBkgAllowanceItems(bkgID: number): Observable<ResBkgAllowanceItems[]> {
    return this.getDynamicAPI('booking/getBkgAllowanceItems', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgAllowanceItems[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        return header.map((v: any) => {
          v.Details = details.filter((d: any) => d.GuestCod === v.GuestCod)
          return new ResBkgAllowanceItems(v)
        })
      })
    );
  }

  getBkgCertificateDetail(params: IResApiGetBookingCertificateDetail): Observable<ResBookingCertificateDetail[]> {
    return this.callDynamicAPI('bkgCertificateDetail/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificateDetail[]>((response) =>
        response.body.map((c: any) => new ResBookingCertificateDetail(c))
      )
    );
  }

  getBkgCertificateTypes(): Observable<ResCertificateType[]> {
    return this.callDynamicAPI('bkgCertificateTypes/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, ResCertificateType[]>((response) =>
        response.body.map((c: any) => new ResCertificateType(c))
      )
    );
  }

  getBkgCertificates(bkgID: number): Observable<ResBookingCertificate[]> {
    return this.callDynamicAPI('bkgCertificate/select', { bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificate[]>((response) => {
        const certificates: ResBookingCertificate[] = response.body.Certificates.map((c: any) => new ResBookingCertificate(c));
        const availableAmounts: ResBookingCertificateAvailableAmount[] = response.body.AvailableAmounts?.map((c: any) => new ResBookingCertificateAvailableAmount(c));
        availableAmounts
          .sort((a, b) => b.defaultValue - a.defaultValue)
          .forEach(a => {
            const certificate = certificates.find(c => a.certificateTypeCod === c.certificateTypeCod);
            if (certificate) {
              (certificate as any)['amountToUseByGuestCod-' + a.guestCod] = Math.min(a.defaultValue, certificate.bookingAvailable);
            }
          })
        return certificates
      }
      )
    );
  }

  getBkgCommunicationEmail(bkgID: number): Observable<ResBkgCommunicationEmail[]> {
    return this.callDynamicAPI('booking/getBkgCommunicationEmail', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgCommunicationEmail[]>(
        (response) => response.body.map((data: any) => new ResBkgCommunicationEmail(data))
      ));
  }

  getBkgCommunicationEmailConfiguration(configurationCod: string): Observable<string> {
    return this.callDynamicAPI('booking/getTypeConfiguration', { ConfigurationCod: configurationCod }).pipe(
      map<DynamicApiResponse, string>(
        (response) => response.body[0].Configuration
      ));
  }

  getBkgCommunicationEmailTemplate(templateID: string): Observable<ResBkgCommunicationEmailTemplate> {
    return this.callDynamicAPI('booking/getBkgEmailTemplate', { TemplateID: templateID }).pipe(
      map<DynamicApiResponse, ResBkgCommunicationEmailTemplate>(
        (response) => new ResBkgCommunicationEmailTemplate(response.body[0])
      ));
  }

  getBkgDeleteMessage(bkgID?: number): Observable<string> {
    return this.getDynamicAPI('bkg/getDeleteMessage', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0].Msg
      })
    );
  }

  getBkgDiscountAnalysys(params: IResApiGetBkgDiscountAnalysysParams): Observable<{ discountAnalysys: ResBkgDiscountAnalysys[], discountGroups: ResBkgDiscountAnalysysGroup[] }> {
    return this.callDynamicAPI('booking/getSequentialDiscount', params).pipe(
      map<DynamicApiResponse, { discountAnalysys: ResBkgDiscountAnalysys[], discountGroups: ResBkgDiscountAnalysysGroup[] }>(
        (response) => {
          return {
            discountAnalysys: response.body.Discounts.map((data: any) => new ResBkgDiscountAnalysys(data)),
            discountGroups: response.body.Groups.map((data: any) => new ResBkgDiscountAnalysysGroup(data))
          }
        }
      ));
  }

  getBkgDmc(bkgID: number): Observable<Array<ResBkgDmc>> {
    return this.callDynamicAPI('booking/dmcList', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ResBkgDmc>>((response) => {
        return response.body.map((data: any) => new ResBkgDmc(data));
      })
    )
  }

  getBkgExtraItems(params: { bkgID: number, serviceID: number, itineraryDate: DateTime }): Observable<ResBkgExtraItem[]> {
    const prms = {
      BkgID: params.bkgID,
      MainServiceID: params.serviceID,
      ItineraryDate: params.itineraryDate
    }
    return this.getDynamicAPI('booking/getExtraBkgItems', prms).pipe(
      map<DynamicApiResponse, ResBkgExtraItem[]>((response) => {
        let objResponse: any = response.body
        if (response.body.DT0) {
          objResponse = response.body.DT0
        }
        return objResponse.map((data: any) => {
          return new ResBkgExtraItem(data)
        })
      }))
  }

  getBkgIncludedItems(bkgID: number): Observable<ResBkgIncludedItems[]> {
    return this.callDynamicAPI('booking/getBkgIncludedItems', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgIncludedItems[]>((response) => response.body.map((data: any) => new ResBkgIncludedItems(data)))
    );
  }

  getBkgIntercompanyList(bkgID: number): Observable<ResBkgIntercompanyList[]> {
    return this.callDynamicAPI('booking/getBkgIntercompanyList', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgIntercompanyList[]>((response) => {
        return response.body.map((data: any) => new ResBkgIntercompanyList(data))
      })
    )
  }

  getBkgJourneyItinerary(params: IResApiGetBkgJourneyItineraryParams): Observable<Array<ResBkgJourneyItinerary>> {
    return this.callDynamicAPI('booking/getBkgJourneyItinerary', params).pipe(
      map<DynamicApiResponse, Array<ResBkgJourneyItinerary>>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItinerary(data));
      })
    )
  }

  getBkgJourneyItineraryHistoryDates(params: IResApiGetBkgJourneyItineraryHistoryParams): Observable<ResBkgJourneyItineraryHistoryDates[]> {
    return this.callDynamicAPI('booking/getBkgItineraryHistory', params).pipe(
      map<DynamicApiResponse, ResBkgJourneyItineraryHistoryDates[]>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItineraryHistoryDates(data))
      })
    )
  }

  getBkgJourneyItineraryHistoryDetails(params: IResApiGetBkgJourneyItineraryHistoryDetailsParams): Observable<ResBkgJourneyItineraryHistory[]> {
    return this.callDynamicAPI('booking/getBkgItineraryHistoryDetail', params).pipe(
      map<DynamicApiResponse, ResBkgJourneyItineraryHistory[]>((response) => {
        return response.body.map((data: any) => new ResBkgJourneyItineraryHistory(data))
      })
    )
  }

  getBkgMultiVoyages(bkgID: number): Observable<ResBookingMultiVoyage[]> {
    return this.callDynamicAPI('booking/getBkgMultiVoyageDropdown', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBookingMultiVoyage[]>(
        (response) => response.body.map((data: any) => new ResBookingMultiVoyage(data))
      ));
  }

  public getBkgNotes(params: IReservationApiGetBkgNotes): Observable<Array<ResBkgNote>> {
    return this.callDynamicAPI('bkgNotes/select', params).pipe(
      map<DynamicApiResponse, Array<ResBkgNote>>((response) => {
        return response.body.map((b: any) => new ResBkgNote(b));
      })
    );
  }

  public getBkgOptionPrices(params: IReservationGetBkgOptionsParams, roomConfig: IServiceOptionGuest2Room[]): Observable<{data:ReservationServiceOption[],totalCount:number}> {
    if (params.DateTo && !params.DateTo.isValid) params.DateTo = undefined;
    if (params.DateFrom && !params.DateFrom.isValid) params.DateFrom = undefined;
    const postParams:any = Object.assign({},params)
    postParams.ServiceOptionGuest2Room = roomConfig

    return this.postDynamicAPI('bkgOption/selectPrice', postParams).pipe(
      map<DynamicApiResponse, { data: any[], totalCount: number }>((response) => {
        let objResponse: any = response.body
        let amenities: any[] = []
        let calendar: any[] = []
        let costGrid: any[] = []
        let cityTaxGrid: any[] = []
        let serviceTaxGrid: any[] = []
        let occupancy: any[] = []
        let usedServiceType: any[] = []
        let internalFligth: any[] = []
        if (response.body.DT0) {
          objResponse = response.body.DT0
          amenities = response.body.DT1
          calendar = response.body.DT2
          costGrid = response.body.DT3
          cityTaxGrid = response.body.DT4
          serviceTaxGrid = response.body.DT5
          occupancy = response.body.DT6
          usedServiceType = response.body.DT7
          internalFligth = response.body.DT8
        }
        return {
          data: objResponse.map((c: IReservationApiServiceOption) => {
            c.Amenities = amenities
            c.Calendar = calendar
            c.CostGrid = costGrid
            c.CityTaxGrid = cityTaxGrid
            c.ServiceTaxGrid = serviceTaxGrid
            c.Occupancy = occupancy
            c.UsedServiceType = usedServiceType
            c.InternalFligth = internalFligth
            return new ReservationServiceOption(c, this.isUatEnv, this.coreFormatService)
          }),
          totalCount: response.recordsCount
        };
      })
    );
  }

  public getBkgOptions(params: IReservationGetBkgOptionsParams, loadOptions?: any): Observable<{data:ReservationServiceOption[],totalCount:number}> {
    if (params.DateTo && !params.DateTo.isValid) params.DateTo = undefined;
    if (params.DateFrom && !params.DateFrom.isValid) params.DateFrom = undefined;
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('bkgOption/select', params).pipe(
      map<DynamicApiResponse, { data: any[], totalCount: number }>((response) => {
        let objResponse: any = response.body
        let amenities: any[] = []
        let calendar: any[] = []
        let costGrid: any[] = []
        let cityTaxGrid: any[] = []
        let serviceTaxGrid: any[] = []
        let occupancy: any[] = []
        let usedServiceType: any[] = []
        let internalFligth: any[] = []
        if (response.body.DT0) {
          objResponse = response.body.DT0
          amenities = response.body.DT1
          calendar = response.body.DT2
          costGrid = response.body.DT3
          cityTaxGrid = response.body.DT4
          serviceTaxGrid = response.body.DT5
          occupancy = response.body.DT6
          usedServiceType = response.body.DT7
          internalFligth = response.body.DT8
        }
        return {
          data: objResponse.map((c: IReservationApiServiceOption) => {
            c.Amenities = amenities
            c.Calendar = calendar
            c.CostGrid = costGrid
            c.CityTaxGrid = cityTaxGrid
            c.ServiceTaxGrid = serviceTaxGrid
            c.Occupancy = occupancy
            c.UsedServiceType = usedServiceType
            c.InternalFligth = internalFligth
            return new ReservationServiceOption(c, this.isUatEnv, this.coreFormatService)
          }),
          totalCount: response.recordsCount
        };
      })
    );
  }

  getBkgPromoDMC(params: IResApiGetPromoDMCParams): Observable<ResBkgPromoDMC[]> {
    return this.callDynamicAPI('booking/getCircuitDiscount', params).pipe(
      map<DynamicApiResponse, ResBkgPromoDMC[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        return header.map((v: any) => {
          v.PromoDMCDetail = details.filter((d: any) => d.CircuitID === v.CircuitID)
          return new ResBkgPromoDMC(v)
        })
      })
    );
  }

  public getBkgServices(params: IReservationGetBkgServicesParams, loadOptions?: any): Observable<any> {
    if (params.DateTo && !params.DateTo.isValid) params.DateTo = undefined;
    if (params.DateFrom && !params.DateFrom.isValid) params.DateFrom = undefined;
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('bkgService/select', params).pipe(
      map<DynamicApiResponse, { data: any[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: IReservationApiService) => new ReservationServiceData(c, this.coreFormatService)),
          totalCount: response.recordsCount
        };
      })
    );
  }

  getBkgSuggestedItems(params: IResApiGetBkgSuggestedItems): Observable<ResBkgSuggestedItem[]> {
    return this.getDynamicAPI('booking/getSuggestedBkgItems', params).pipe(
      map<DynamicApiResponse, ResBkgSuggestedItem[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        return header.map((v: any) => {
          v.Details = details.filter((d: any) => d.SuggestedItemID === v.SuggestedItemID)
          return new ResBkgSuggestedItem(v)
        })
      })
    );
  }

  getBkgTemplateAllTags(): Observable<ResBkgTemplateTag[]> {
    return this.callDynamicAPI('bkgtemplate/alltags').pipe(
      map<DynamicApiResponse, ResBkgTemplateTag[]>((response) => {
        const rows = Array.isArray(response.body) ? response.body : (response.body?.DT0 ?? [])
        return rows.map((data: any) => new ResBkgTemplateTag(data))
      })
    );
  }

  public getBkgTouch(bkgID?: number): Observable<Array<ResBkgNoteTouch>> {
    return this.callDynamicAPI('bkgNotes/getNoteTouch', { KeyCod: bkgID }).pipe(
      map<DynamicApiResponse, Array<ResBkgNoteTouch>>((response) => {
        return response.body.map((b: any) => new ResBkgNoteTouch(b));
      })
    );
  }

  getBkgWishlist(params: { BkgID: number }): Observable<ResBkgWishlist[]> {
    return this.callDynamicAPI('bkgWishlist/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResBkgWishlist[]>((response) =>
        response.body.map((c: any) => new ResBkgWishlist(c))
      )
    );
  }

  /**
   * Retrieves all booking agent owners
   *
   * @return {*}  {Observable<Array<any>>}
   * @memberof ReservationBookingApiService
   */
  public getBookingAgentOwners(bkgID: number, search?: string, id?: string): Observable<Array<any>> {
    let params: any = { BkgID: bkgID };
    if (!!search) {
      params.SearchText = search;
    }
    if (!!id) {
      params.Bkg_Agent_Owner = id;
    }
    return this.callDynamicAPI('booking/agentowners', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  getBookingBrands(): Observable<{ brandID: number, brandName: string }[]> {
    return this.callDynamicAPI('bkg/getBrands').pipe(
      map<DynamicApiResponse, { brandID: number, brandName: string }[]>(
        (response) => response.body.map((data: any) => { return { brandID: data.BrandID, brandName: data.BrandName } })
      ));
  }

  /**
   * Retrieves booking cancel reasons list
   *
   * @return {*}  {Observable<ResBookingCancelReason>}
   * @memberof ReservationBookingApiService
   */
  getBookingCancelReason(bkgID?: number): Observable<Array<ResBookingCancelReason>> {
    return this.callDynamicAPI('booking/cancelReason', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, Array<ResBookingCancelReason>>((response) => response.body.map((r: any) => new ResBookingCancelReason(r))),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }

  getBookingCreatedBy(bookingID: number): Observable<BookingCreatedBy[]> {
    return this.getDynamicAPI('booking/createdByModifyUsers', { BkgID: bookingID }).pipe(
      map<DynamicApiResponse, BookingCreatedBy[]>((response) => {
        return response.body.map((data: any) => new BookingCreatedBy(data))
      })
    );
  }

  /**
   * Retrieves booking financials
   *
   * @param {number} bkgID
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  public getBookingFinancials(bkgID: number) {
    return this.getDynamicAPI('booking/financial', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ResBookingFinancialRecap>>((response) => {
        return response.body.Financials.map((i: any) => new ResBookingFinancialRecap(i));
      })
    );
  }

  /**
   * Retrieves all booking possible groups
   *
   * @param {{ BkgID: number, GrpID?: number }} params
   * @return {*}  {Observable<Array<IResBookingGroup>>}
   * @memberof ReservationBookingApiService
   */
  public getBookingGroups(params: { BkgID: number, GrpID?: number }): Observable<Array<IResBookingGroup>> {
    return this.callDynamicAPI('booking/groups', params).pipe(
      map<DynamicApiResponse, Array<IResBookingGroup>>((response) => {
        return response.body as IResBookingGroup[];
      })
    );
  }

  getBookingLanguage(): Observable<ResBookingLanguage[]> {
    return this.callDynamicAPI('booking/getLanguageBooking').pipe(
      map<DynamicApiResponse, ResBookingLanguage[]>(
        (response) => response.body.map((data: any) => new ResBookingLanguage(data))
      ));
  }

  public getBookingMarginality(bkgID?: number): Observable<ReservationBookingMarginality> {
    return this.callDynamicAPI('booking/marginality', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ReservationBookingMarginality>((response) => {
        return response.body.map((data: any) => new ReservationBookingMarginality(data));
      })
    )
  }

  getBookingPayerDetails(bkgID: number, payerID:number, guestID: number, onlyWithPrice = true): Observable<ResBookingPayerDetails[]> {
    const hiddenWithoutPrice = onlyWithPrice ? 'Y' : 'N'
    return this.callDynamicAPI('payment/getPayerItemList', { BkgID: bkgID, PayerID: payerID, GuestID: guestID, HiddenWithoutPrice: hiddenWithoutPrice }).pipe(
      map<DynamicApiResponse, ResBookingPayerDetails[]>((response) => {
        return response.body.map((data: any) => (new ResBookingPayerDetails(data)));
      })
    );
  }

  getBookingPayerManagement(bkgID: number): Observable<{ payers: ResBookingPayerMngmtPayers[], groups: ResBookingPayerMngmtGroups[], guests: ResBookingPayerMngmtGuests[], relations: ResBookingPayerMngmtRelations[], roles: ResBookingPayerMngmtRoles[] }> {
    return this.callDynamicAPI('payment/getPayerManagement', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, { payers: ResBookingPayerMngmtPayers[], groups: ResBookingPayerMngmtGroups[], guests: ResBookingPayerMngmtGuests[], relations: ResBookingPayerMngmtRelations[], roles: ResBookingPayerMngmtRoles[] }>((response) => {
        const res = {
          payers: response.body.DT0.map((data: any) => (new ResBookingPayerMngmtPayers(data))) as ResBookingPayerMngmtPayers[],
          groups: response.body.DT1.map((data: any) => (new ResBookingPayerMngmtGroups(data))) as ResBookingPayerMngmtGroups[],
          guests: response.body.DT2.map((data: any) => (new ResBookingPayerMngmtGuests(data))) as ResBookingPayerMngmtGuests[],
          relations: response.body.DT3.map((data: any) => (new ResBookingPayerMngmtRelations(data))) as ResBookingPayerMngmtRelations[],
          roles: response.body.DT4.map((data: any) => (new ResBookingPayerMngmtRoles(data)))
        }
        res.payers.forEach(p=>{
          const guest = res.guests.find(g=>g.guestID===p.guestID)
          if(guest) {
            p.guestName=guest.guestName
          }
          res.groups.forEach(g=>{
            const guestsID = new Set<number>()
            res.relations.filter(r=>r.paxGroupID===g.paxGroupID && r.payerID===p.payerID).forEach(g=>guestsID.add(g.guestID))
            const guests = res.guests.filter(g=>guestsID.has(g.guestID))
            p.guestByGroup.push({
              paxGroupID: g.paxGroupID,
              guests: guests
            })

          })
        })
        res.relations.forEach(r=>{
          const exist = res.guests.find(g=>g.guestID===r.guestID && g.paxGroupID === r.paxGroupID && g.effectivePayerID === r.payerID)
          if(!exist) {
            const guestModel = res.guests.find(g=>g.paxGroupID===r.paxGroupID && g.guestID===r.guestID)
            if(guestModel) {
              const vGuest = new ResBookingPayerMngmtGuests({
                virtual: true,
                guestID: guestModel.guestID,
                guestCod: guestModel.guestCod,
                guestName: guestModel.guestName,
                paxGroupID: guestModel.paxGroupID,
                effectivePayerID: r.payerID,
                payerName: res.payers.find(p=>p.payerID===r.payerID)?.payerName ?? `Payer ${r.payerID}`
              })
              res.guests.push(vGuest)
            }

          }

        })
        res.guests
          .sort((g1,g2)=>{
            const group=g1.paxGroupID - g2.paxGroupID
            if(group!=0) return group
            const code = +g1.guestCod - +g2.guestCod
            if(code!=0) return code
            return g1.virtual ? 0 : -1
          })
        return res;
      })
    );
  }

  public getBookingSaleRequests(bkgId: number): Observable<BookingSaleRequest[]> {
    return this.callDynamicAPI('booking/saleRequest', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, BookingSaleRequest[]>((response) => {
        const request = response.body.DT0 ?? []
        request.forEach((r:any)=>{
          r.Remarks = (response.body.DT1 ?? []).filter((d: any) => d.RequestId === r.RequestId)
        })
        return request.map((data: any) => new BookingSaleRequest(data));
      })
    )
  }

  public getBookingToRequests(bkgId: number): Observable<BookingToRequest[]> {
    return this.callDynamicAPI('booking/toRequest', { BkgID: bkgId }).pipe(
      map<DynamicApiResponse, BookingToRequest[]>((response) => {
        return response.body.map((data: any) => new BookingToRequest(data));
      })
    )
  }

  public getCompanyAgents(companyID: number): Observable<{ CompanyID: number; UserID: number; Login: string; Display_name: string }[]> {
    return this.callDynamicAPI('company/getAgents', { CompanyID: companyID }).pipe(
      map<DynamicApiResponse, { CompanyID: number; UserID: number; Login: string; Display_name: string }[]>((response) => {
        return response.body ?? [];
      })
    );
  }

  getDMCDocCheck(bkgID: number, fullCheck = false): Observable<ResDMCDocCheck[]> {
    return this.callDynamicAPI('booking/getCheckDMCSupplierList', { BkgID: bkgID, FullCheck: fullCheck ? 'Y' : 'N' }).pipe(
      map<DynamicApiResponse, ResDMCDocCheck[]>((response) => response.body.map((data: any) => new ResDMCDocCheck(data)))
    );
  }

  public getDMCReference(bkgID: number): Observable<IReservationApiDMCReference[]> {
    return this.callDynamicAPI('booking/getDMCReference', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, IReservationApiDMCReference[]>((response) => {
        return response.body ?? [];
      })
    );
  }

  getDayByDayAllImagesByCategory(params: IResApiGetDayByDayAllImagesByCategoryParams): Observable<ResBookingDayByDayAllImages[]> {
    return this.callDynamicAPI('booking/getBkgDayByDayAllImages', params).pipe(
      map<DynamicApiResponse, ResBookingDayByDayAllImages[]>(
        (response) => response.body.map((data: any) => new ResBookingDayByDayAllImages(data))
      ));
  }

  getDayByDayImages(params: IResApiGetDayByDayImagesParams): Observable<ResBookingDayByDayImages[]> {
    return this.callDynamicAPI('booking/getBkgDayByDayImages', params).pipe(
      map<DynamicApiResponse, ResBookingDayByDayImages[]>(
        (response) => response.body.map((data: any) => new ResBookingDayByDayImages(data))
      ));
  }

  getDayByDayInclusions(bkgID: number, dayByDayTypeID = 1): Observable<ResBookingInclusionsDayByDay[]> {
    return this.callDynamicAPI('booking/getBkgDayByDayInclusion', { BkgID: bkgID, BookingDayByDayServiceDetailTypeID: dayByDayTypeID}).pipe(
      map<DynamicApiResponse, ResBookingInclusionsDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingInclusionsDayByDay(data))
      ));
  }

  getDayByDayNotesItinerary(bkgID: number): Observable<ResBookingNotesDayByDay[]> {
    return this.callDynamicAPI('booking/getDayByDayNotes', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBookingNotesDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingNotesDayByDay(data))
      ));
  }

  getDayByDaySuggestedNotes(bkgID: number): Observable<ResBookingDayByDayNotesSuggestion[]> {
    return this.callDynamicAPI('booking/getDayByDaySuggestedNotes', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBookingDayByDayNotesSuggestion[]>(
        (response) => response.body.map((data: any) => new ResBookingDayByDayNotesSuggestion(data))
      ));
  }

  getDayByDayPackages(params: IResApiGetDayByDayPackagesParams): Observable<ResBookingPackageDayByDay[]> {
    return this.callDynamicAPI('booking/getDayByDayPackage', params).pipe(
      map<DynamicApiResponse, ResBookingPackageDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingPackageDayByDay(data))
      ));
  }

  getDayByDayTopicHistory(bkgID: number): Observable<ResBkgTopicHistoryDates[]> {
    return this.callDynamicAPI('booking/getTopicHistory', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgTopicHistoryDates[]>(
        (response) => response.body.map((data: any) => new ResBkgTopicHistoryDates(data))
      ));
  }

  getDayByDayTopicHistoryDetail(params: IResApiGetBkgTopicHistoryDetailsParams): Observable<ResBkgTopicHistoryDetail[]> {
    return this.callDynamicAPI('booking/getTopicHistoryDetail', params).pipe(
      map<DynamicApiResponse, ResBkgTopicHistoryDetail[]>(
        (response) => response.body.map((data: any) => new ResBkgTopicHistoryDetail(data))
      ));
  }

  getDayByDayTopicItinerary(params: IResApiGetDayByDayTopicItineraryParams): Observable<ResBookingTopicDayByDay[]> {
    return this.callDynamicAPI('booking/getTopicDayByDay', params).pipe(
      map<DynamicApiResponse, ResBookingTopicDayByDay[]>(
        (response) => response.body.map((data: any) => new ResBookingTopicDayByDay(data))
      ));
  }

  public getDebugBkgItinerary(bkgID: number): Observable<ResDebugBkgItinerary> {
    return this.getDynamicAPI('booking/getDebugBkgItinerary', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResDebugBkgItinerary>((response) => new ResDebugBkgItinerary(response.body))
    );
  }

  public getDebugPkgItinerary(packageID: number): Observable<ResDebugBkgItinerary> {
    return this.getDynamicAPI('package/getDebugPkgItinerary', { PackageID: packageID }).pipe(
      map<DynamicApiResponse, ResDebugBkgItinerary>((response) => new ResDebugBkgItinerary(response.body))
    );
  }

  getDecks(params: { Wishlist: ReservationApiBoolean }): Observable<ResDeck[]> {
    return this.callDynamicAPI('deck/select', params, 'GET').pipe(
      map<DynamicApiResponse, ResDeck[]>((response) =>
        response.body.map((c: any) => new ResDeck(c))
      )
    );
  }

  getDmcEDocUrl(bkgID: number, languageCode?: string, showFinancial: string = 'N', guestCopy: string = 'N'): Observable<string> {
    return this.getDynamicAPI('booking/getDmcEDocUrl', { BkgID: bkgID, LanguageCode: languageCode, ShowFinancial: showFinancial, GuestCopy: guestCopy }).pipe(
      map<DynamicApiResponse, string>((response) => {
        return response.body[0]?.Url ?? ''
      })
    );
  }

  getDmcManage(bkgID: number): Observable<any> {
    return this.getDynamicAPI('booking/dmcManage', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    );
  }

  getEresIntegrationCheck(bkgID: number): Observable<ResEresIntegrationCheck[]> {
    return this.getDynamicAPI('booking/getEresIntegrationCheck', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResEresIntegrationCheck[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        return header.map((v: any) => {
          v.Details = details.filter((d: any) => d.RequestID === v.RequestID)
          return new ResEresIntegrationCheck(v)
        })
      })
    );
  }

  getExperienceOptional(bkgID: number, experienceID: number): Observable<ResExperienceOptional[]> {
    return this.callDynamicAPI('experience/optional',
      {
        BkgID: bkgID,
        ExperienceID: experienceID
      })
      .pipe(
        map<DynamicApiResponse, ResExperienceOptional[]>((response) => {
          return response.body.map((d: any) => new ResExperienceOptional(d))
        })
      );
  }

  getExportInvoiceData(params?: IResApiGetExportInvoiceDataParams): Observable<ResAccountingExportData[]> {
    return this.getDynamicAPI('payable/getAccountingExport', params).pipe(
      map<DynamicApiResponse, ResAccountingExportData[]>((response) => {
        return response.body.map((data: any) => new ResAccountingExportData(data))
      })
    );
  }

  getExportItemBySuppliersExcel(bkgID: number, groupBy: string, pageSize: number): Observable<any> {
    return this.postDynamicAPI('booking/items', { BkgID: bkgID, GroupBy: groupBy, PageSize: pageSize }).pipe(
      map<DynamicApiResponse, any>((response) => response.body.items)
    );
  }

  getExternalGifUrl(params: IReservationApiBookingExternalGifUrlParameters) {
    return this.getExternalEmAPI(`mb/s/BackDoor/${params.website}`, 'POST', params).pipe(
      map<any, string>(res => {
        const url = res.Body.url ?? '';
        return url;
      })
    );
  }

  getFccCertificate(bkgID?: number): Observable<ResFccCalculation[]> {
    return this.callDynamicAPI('fcc/calculationGet', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, ResFccCalculation[]>((response) =>
        response.body.map((r: any) => new ResFccCalculation(r))
      ),
      catchError((_response: IDynamicApiResponse) => {
        return [];
      })
    );
  }

  getFinancialSplit(bkgID: number, section: string): Observable<Array<ResFinancialSplit>> {
    return this.callDynamicAPI('financial/split', { BkgID: bkgID, Section: section, IsFe: 'Y' }).pipe(
      map<DynamicApiResponse, Array<ResFinancialSplit>>((response) => {
        return response.body.map((data: any) => new ResFinancialSplit(data));
      })
    )
  }

  getFinancialSplitMultiCurrency(bkgID: number): Observable<Array<ResFinancialSplitMultiCurrency>> {
    return this.callDynamicAPI('booking/getFinancialMultiBkgCurrency', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, Array<ResFinancialSplitMultiCurrency>>((response) => {
        return response.body.map((data: any) => new ResFinancialSplitMultiCurrency(data));
      })
    )
  }

  getPaymentMilestone(bkgID: number): Observable<Array<ResPaymentMilestone>> {
    return this.callDynamicAPI('booking/getPaymentMilestone', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, Array<ResPaymentMilestone>>((response) => {
        return response.body.map((data: any) => new ResPaymentMilestone(data));
      })
    )
  }

  managePaymentMilestone(params: IResManagePaymentMilestoneParams): Observable<any> {
    return this.postDynamicAPI('booking/managePaymentMilestone', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    )
  }

  public getGuestAirData(params: { BkgID: number }): Observable<Array<BookingGuestAirData>> {
    return this.callDynamicAPI('guestGTW/select', params).pipe(
      map<DynamicApiResponse, Array<BookingGuestAirData>>((response) => {
        return response.body.map((d: any) => new BookingGuestAirData(d));
      })
    );
  }

  getGuestTypesForBkg(params: { BkgID: number }): Observable<IReservationLoyaltyDiscount[]> {
    return this.callDynamicAPI('booking/guestTypes', params, 'GET').pipe(
      map<DynamicApiResponse, IReservationLoyaltyDiscount[]>((response) => response.body)
    );
  }

  public getInvoiceSendingTypes() {
    return this.callDynamicAPI('invoiceSendingType/select', undefined, 'GET').pipe(
      map<DynamicApiResponse, { value: string; label: string }[]>((response) => {
        return response.body.map((t: { Key: string; Label: string }) => ({ value: t.Key, label: t.Label }));
      })
    );
  }

  public getInvoiceTypes(BkgID?: number) {
    return this.callDynamicAPI('invoiceType/select', { BkgID }, 'GET').pipe(
      map<DynamicApiResponse, { value: string; label: string }[]>((response) => {
        return response.body.map((t: { Key: string; Label: string }) => ({ value: t.Key, label: t.Label }));
      })
    );
  }

  getMainDestination(): Observable<ResBkgMainDestination[]> {
    return this.getDynamicAPI('bkg/getMainDestination').pipe(
      map<DynamicApiResponse, ResBkgMainDestination[]>((response) => {
        return response.body.map((data: any) => new ResBkgMainDestination(data))
      })
    );
  }

  public getMarkets(): Observable<Array<Market>> {
    return this.callDynamicAPI('market/list').pipe(
      map<DynamicApiResponse, Array<Market>>((response) => {
        return response.body.map((data: any) => new Market(data));
      })
    )
  }

  getMiniPackageBkgList(bkgID: number): Observable<ResMiniPackageBkgList[]> {
    return this.callDynamicAPI('booking/getMiniPackageBkgList', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResMiniPackageBkgList[]>((response) => response.body.map((data: any) => new ResMiniPackageBkgList(data)))
    );
  }

  public getMultiBooking(params: IReservationApiGetMultiBookingParams): Observable<ResMultiBookingItem[]> {
    return this.callDynamicAPI('item/getMultiBooking', params).pipe(
      map<DynamicApiResponse, ResMultiBookingItem[]>((response) => {
        return Array.isArray(response.body) ? response.body.map((row: any) => new ResMultiBookingItem(row)) : [];
      })
    );
  }

  public getInvoicePackages(params?: IReservationApiGetInvoicePackagesParams): Observable<ResMultiBookingInvoicePackage[]> {
    return this.callDynamicAPI('multibooking/getInvoicePackages', params).pipe(
      map<DynamicApiResponse, ResMultiBookingInvoicePackage[]>((response) => {
        return Array.isArray(response.body) ? response.body.map((row: any) => new ResMultiBookingInvoicePackage(row)) : [];
      })
    );
  }

  getNoteTypes(bkgID?: number): Observable<IReservationNoteType[]> {
    return this.callDynamicAPI('noteTypes/select', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, IReservationNoteType[]>((response) => {
        return response.body as IReservationNoteType[];
      })
    )
  }

  public getPriceTypes(params: { Type?: 'OD' | 'ChangePriceType', BkgID?: number }): Observable<Array<IReservationPriceType>> {
    return this.callDynamicAPI('products/pricetypes', params).pipe(
      map<DynamicApiResponse, Array<IReservationPriceType>>((response) => {
        return response.body?.PriceTypes;
      })
    );
  }

  getReinstateItemList(bkgID: number): Observable<BookingJourneySupplement[]> {
    return this.callDynamicAPI('booking/getReinstateItemList', { BkgID: bkgID }, 'GET').pipe(
      map<DynamicApiResponse, BookingJourneySupplement[]>((response) => response.body.map((i: IResApiReinstateItem) => createJourneySupplementFromReinstateItem(i)))
    );
  }

  getRightRezOfficeIdConfig(): Observable<ResRightRezOfficeIdConfig[]> {
    return this.callDynamicAPI('rightrez/officeIdConfig',
      {},
      'GET').pipe(
      map<DynamicApiResponse, Array<ResRightRezOfficeIdConfig>>((response) => {
        return response.body.map((cfg: any) => new ResRightRezOfficeIdConfig(cfg));
      })
    );
  }

  getSalesAssistant(bkgID?: number): Observable<ResBkgSalesAssistant[]> {
    return this.getDynamicAPI('editBkg/getSalesAssistant', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgSalesAssistant[]>((response) => {
        return response.body.map((data: any) => new ResBkgSalesAssistant(data))
      })
    );
  }

  getSavedFlights(params: IResApiGetAirFlightParams): Observable<ResAirInternalFlight[]> {
    return this.callDynamicAPI('air/getInternalFlight', params).pipe(
      map<DynamicApiResponse, ResAirInternalFlight[]>(
        (response) => response.body.map((data: any) => new ResAirInternalFlight(data))
      ));
  }

  getSavedFutureFlights(params: IResApiGetAirFlightParams): Observable<ResAirFutureFlight[]> {
    return this.callDynamicAPI('air/getInternalFlight', params).pipe(
      map<DynamicApiResponse, ResAirFutureFlight[]>(
        (response) => response.body.map((data: any) => new ResAirFutureFlight(data))
      ));
  }

  getSecondarySalesAgent(agencyCod?: string): Observable<ResBkgSecondarySalesAgent[]> {
    return this.getDynamicAPI('editBkg/getSecondarySalesAgent', { AgencyCod: agencyCod }).pipe(
      map<DynamicApiResponse, ResBkgSecondarySalesAgent[]>((response) => {
        return response.body.map((data: any) => new ResBkgSecondarySalesAgent(data))
      })
    );
  }

  getSecondaryTravelAgency(bkgID?: number): Observable<ResBkgSecondaryTravelAgency[]> {
    return this.getDynamicAPI('editBkg/getSecondaryTravelAgent', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgSecondaryTravelAgency[]>((response) => {
        return response.body.map((data: any) => new ResBkgSecondaryTravelAgency(data))
      })
    );
  }

  getSegment(): Observable<ResBkgSegment[]> {
    return this.getDynamicAPI('bkg/getSegment').pipe(
      map<DynamicApiResponse, ResBkgSegment[]>((response) => {
        return response.body.map((data: any) => new ResBkgSegment(data))
      })
    );
  }

  getSubSegment(segmentID?: number): Observable<ResBkgSubSegment[]> {
    return this.getDynamicAPI('bkg/getSubSegment', { SegmentID: segmentID }).pipe(
      map<DynamicApiResponse, ResBkgSubSegment[]>((response) => {
        return response.body.map((data: any) => new ResBkgSubSegment(data))
      })
    );
  }

  public getSuitePriceCategories(params: { BkgID: number, SectionID: number }): Observable<Array<ResSuitePriceCategory>> {
    return this.callDynamicAPI('suitePriceCategory/select', params).pipe(
      map<DynamicApiResponse, Array<ResSuitePriceCategory>>((response) => {
        return response.body.map((d: any) => new ResSuitePriceCategory(d));
      })
    );
  }

  getSupplierEditRequestDetails(params: IResApiSupplierEditRequest): Observable<ResSupplierEditRequestDetails[]> {
    return this.getDynamicAPI('supplier/getSupplierServiceItem', params).pipe(
      map<DynamicApiResponse, ResSupplierEditRequestDetails[]>((response) => {
        return response.body.map((data: any) => new ResSupplierEditRequestDetails(data))
      }))
  }

  getSyncMiniPackage(bkgID?: number): Observable<any> {
    return this.callDynamicAPI('booking/getSyncMiniPackage', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  getVersionHistory(bkgID: number): Observable<ResBkgVersionHistory[]> {
    return this.callDynamicAPI('booking/getTMVersionHistory', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, ResBkgVersionHistory[]>((response) => {
        const header = response.body.DT0 ?? []
        const details = response.body.DT1 ?? []
        const totals = response.body.DT2 ?? []
        return header.map((v: any) => {
          v.ChildDetails = details.filter((d: any) => d.OptionIDFh === v.OptionID && d.ItineraryDate === v.ItineraryDate && d.BkgID === v.BkgID)
          v.Totals = totals.filter((d: any) => d.BkgID === v.BkgID)
          return new ResBkgVersionHistory(v)
        })
      })
    );
  }

  getVillaAvailability(params: IResVillaAvailabilityParams): Observable<ResVillaAvailability[]> {
    return this.getDynamicAPI('villa/details', params).pipe(
      map<DynamicApiResponse, ResVillaAvailability[]>((response) => {
        return response.body.map((data: any) => new ResVillaAvailability(data))
      })
    );
  }

  public guestCheckBookingExists(params: IResApiGuestCheckBookingExistsParams): Observable<{ current: IResApiGuestCheckBookingExistsResult[], next: IResApiGuestCheckBookingExistsResult[], previous: IResApiGuestCheckBookingExistsResult[] }> {
    return this.callDynamicAPI('individual/checkBookingExists', params, 'POST').pipe(
      map<DynamicApiResponse, { current: IResApiGuestCheckBookingExistsResult[], next: IResApiGuestCheckBookingExistsResult[], previous: IResApiGuestCheckBookingExistsResult[] }>((response) => {
        return { current: response.body.Current, next: response.body.Next, previous: response.body.Previous };
      }
      ))
  }

  importBkgJourneyFromProposal(params: IResApiGetBkgJourneyItineraryHistoryParams) {
    return this.postDynamicAPI('booking/importBkgItinerary', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  importCertificates(params: IResApiImportCertificate): Observable<any> {
    return this.postDynamicAPI('certificate/import', params);
  }

  /**
   * Import external A&K TravelStudio airs in OneIS
   *
   * @param {number} bkgID
   * @param {{ BkgID: string, SiteID: number }[]} params
   * @return {*}  {Observable<any>}
   * @memberof ReservationBookingApiService
   */
  importExternalAirs(bkgID: number, params: { BkgID: string, SiteID: number }[]): Observable<any> {
    return this.postDynamicAPI('booking/importExternalAirs', { BkgID: bkgID, BookingList: params });
  }

  importFromPNR(params: IReservationApiImportPNRParams): Observable<{ Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/import',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<any[], { Error?: string }>((response) => {
        if (response.length > 0 && (response[0]?.ErrorCod?.trim() ?? '') !== '') {
          const errorMessages = response
            .filter((item) => item.ErrorDesc && item.ErrorDesc.trim() !== '')
            .map((item) => item.ErrorDesc)
            .join('\n');

          if (errorMessages) {
            return { Error: errorMessages };
          }
        }

        return {};
      }),
      catchError((error: any) => {
        return this.handleGenericError(error);
      })
    );
  }

  intraBookingImport(params: IResApiIntraBookingImportParams) {
    return this.callDynamicAPI('booking/intraBookingImport', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageBkgAllowanceItems(params: IResApiManageBkgIncludedItemsParams): Observable<any> {
    return this.postDynamicAPI('booking/manageIncludedAllowance', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageBkgIncludedItems(params: IResApiManageBkgIncludedItemsParams): Observable<any> {
    return this.postDynamicAPI('booking/manageBkgIncludedItems', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageBkgJourneyItinerary(params: IResApiManageBkgJourneyItinerary) {
    return this.postDynamicAPI('booking/manageBkgJourneyItinerary', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageBkgLinks(params: IResApiLinkedBookingManageParams): Observable<(any[] | {})> {
    return this.callDynamicAPI('booking/upsertLinks', params, 'POST').pipe(
      map<DynamicApiResponse, any[] | {}>((response) => response.body)
    );
  }

  public manageBkgNotes(params: IReservationApiManageBkgNotes): Observable<any> {
    return this.postDynamicAPI('bkgNotes/manage', params);
  }

  manageBkgWishlist(params: IReservationApiManageBkgWishlistParams): Observable<any> {
    return this.postDynamicAPI('bkgWishlist/manage', params);
  }

  manageBookingPayerManagement(params: IResApiManageBookingPayerManagementParams): Observable<ResMaganeBookingPayerResponse[]> {
    return this.postDynamicAPI('payment/managePayerManagement', params).pipe(
      map<DynamicApiResponse, ResMaganeBookingPayerResponse[]>((response) => response.body.map((data: any) => new ResMaganeBookingPayerResponse(data)))
    );
  }

  manageCircuitDiscount(params?: IResApiGetPromoDMCParams): Observable<any> {
    return this.postDynamicAPI('booking/manageCircuitDiscount', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageConfirmSuiteStatus(params: IResBookingManageConfirmSuiteStatusParams): Observable<any> {
    return this.postDynamicAPI('booking/manageConfirmSuiteStatus', params);
  }

  public manageDMCReference(params: IReservationApiManageDMCReferenceParams): Observable<any> {
    return this.postDynamicAPI('booking/manageDMCReference', params);
  }

  manageEditAgeGuest(params: { BkgID: number; GuestID: number; Age: number }): Observable<any> {
    return this.postDynamicAPI('booking/manageEditAgeGuest', params);
  }

  manageDayByDayImages(params: IResApiManageDayByDayImagesParams): Observable<any> {
    return this.postDynamicAPI('booking/manageBkgDayByDayImages', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageDayByDayInclusions(params: IResApiManageDayByDayInclusionsParams): Observable<any> {
    return this.postDynamicAPI('booking/manageBkgDayByDayInclusion', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageDayByDayNotesItinerary(params: IResApiManageDayByDayNotesItinerary): Observable<any> {
    return this.postDynamicAPI('booking/manageDayByDayNotes', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageDayByDayPackages(params: IResApiManageDayByDayPackagesParams): Observable<any> {
    return this.postDynamicAPI('booking/manageDayByDayPackage', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  manageDayByDayTopicItinerary(params: IResApiManageDayByDayTopicItinerary): Observable<any> {
    return this.postDynamicAPI('booking/manageTopicDayByDay', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  public manageFlagsSupplements(params: IResManageMultipleEditItem) {
    return this.postDynamicAPI('booking/multipleEditItem', params);
  }

  manageFutureFlights(params?: IResApiFutureFlights) {
    return this.postDynamicAPI('bookingAir/manageFutureFlight', params);
  }

  manageInternalFlights(params?: IResApiManualFlights) {
    return this.postDynamicAPI('air/manageInternalFlight', params);
  }

  manageJetSeats(params: IResApiManageJetSeats) {
    return this.postDynamicAPI('bkg/manageJetSeats', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  manageManualFlights(params?: IResApiManualFlights) {
    return this.postDynamicAPI('air/manageManualFlight', params);
  }

  manageMultiItems(bkgID: number, items: IResApiBkgItemsManage[], source?: string, tailorShift?: string) {
    return this.callDynamicAPI('booking/manageMultiItems', { BkgID: bkgID, EditItem: items, Source: source, TailorShift: tailorShift }, 'POST');
  }

  managePayerDetailItems(params: IResPayerItemsManageParams): Observable<any> {
    return this.callDynamicAPI('payment/managePayerDetailItems', params, 'POST')
      .pipe(
        map<DynamicApiResponse, any>((response) => response.body)
      );
  }

  saveRoomConfig(bkgID: number, rooms: IResApiSaveRoomConfigRoom[]): Observable<DynamicApiResponse> {
    return this.callDynamicAPI('booking/saveRoomConfig', { BkgID: bkgID, Rooms: rooms }, 'POST');
  }

  manageReinstateItemList(bkgID: number): Observable<DynamicApiResponse> {
    return this.callDynamicAPI('booking/manageReinstateItemList', { BkgID: bkgID }, 'POST');
  }

  manageSupplierItemRequest(params?: IResApiManageSupplierItemRequestListItem) {
    return this.postDynamicAPI('supplier/manageSupplierItemRequest', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  manageSyncDayByDayInclusions(bkgID: number, dayByDayTypeID = 1): Observable<any> {
    return this.postDynamicAPI('booking/manageSyncBkgInclusion', { BkgID: bkgID, BookingDayByDayServiceDetailTypeID: dayByDayTypeID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * Post booking version request params to recalculate a booking
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  recalculateBooking(params: IReservationApiBookingRequestParameters) {
    return this.callDynamicAPI('booking/recalculate', Object.assign({ Action: 'RePrice' }, params), 'POST');
  }

  refreshAirPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'gmt/summarypnr',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<{ pnrs: IReservationApiAirPnr[], Error?: string }, { pnrs: ReservationAirPnr[], Error?: string }>((response) => {
        return {
          pnrs: response.pnrs?.map((pnr: any) => new ReservationAirPnr(pnr)),
          Error: response.Error
        }
      }),
      catchError(({ error: { Error } }) => {
        return of({ pnrs: [], Error })
      })
    );
  }

  refreshAirRRPnrs(params: IReservationApiRefreshAirResultPnrsParams): Observable<{ pnrs: ReservationAirPnr[], Error?: string }> {

    const commandParams = new CoreBaseApiCommandParams({
      url: 'oem/rightrez/update',
      data: params,
      type: 'POST',
      customBaseUrl: this.pluginUrlAPI
    });

    return this.sendCommand(commandParams).pipe(
      map<any, { pnrs: any, Error?: string }>((response): any => {
        const row = response[0]?.AirLineRecordLocators?.some((data: any) => !!data.ErrorCod);
        const hasError = !!row;

        if (hasError) {
          return {
            pnrs: null,
            Error: (response[0]?.AirLineRecordLocators?.filter((data: any) => !!data.ErrorCod).map((data: any) => data.ErrorDesc) ?? '').join('\n')
          }
        }
        return {
          pnrs: response,
          Error: null
        }
      }),
      catchError(({ error: { Error } }) => {
        return of({ pnrs: [], Error })
      })
    );
  }

  releaseOrDeleteAirRRPnr(params: IReservationApiDeleteAirPnrParams): Observable<string> {
    if (!params.button) params.button = 'Release';
    return this.postDynamicAPI('airPnr/delete', params).pipe(map<{ Error?: string }, string>((response) => response?.Error || ''))
  }

  resetDayByDayImages(params: IResApiResetDayByDayImagesParams): Observable<any> {
    return this.callDynamicAPI('booking/resetDayByDayImages', params).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  resetDayByDayTopicItinerary(bkgID: number): Observable<any> {
    return this.callDynamicAPI('booking/resetTopicDayByDay', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  /**
   * Save OD notes
   *
   * @param {number} bkgID
   * @param {string} newNote
   * @return {*}
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  saveBookingNotes(bkgID: number, note: string) {
    return this.postDynamicAPI('booking/upsertHeaderNote', { bkgID, note });
  }

  searchCertificates(params: IResApiSearchCertificate): Observable<ResBookingCertificate[]> {
    return this.callDynamicAPI('certificate/search', params, 'GET').pipe(
      map<DynamicApiResponse, ResBookingCertificate[]>((response) =>
        response.body.map((c: any) => new ResBookingCertificate(c))
      )
    );
  }

  sendDOCAutoInfo(bkgID: number): Observable<any> {
    return this.callDynamicAPI('booking/sendDOCAutoInfo', { BkgID: bkgID }).pipe(
      map<DynamicApiResponse, any>((response) => response.body)
    );
  }

  syncIntercompanyBooking(params: IResApiSyncIntercompanyBookingParams) {
    return this.callDynamicAPI('booking/syncIntercompanyBooking', params).pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body
      })
    )
  }

  public updateGuestAirData(params: IReservationApiUpdateBookingGuestAirData): Observable<any> {
    return this.postDynamicAPI('guestGTW/update', params);
  }

  public updateMessage(params: IReservationUpdateMessageParams) {
    return this.postDynamicAPI('message/update', params);
  }

  public updateSuitePriceCategory(params: IReservationApiUpdateSuiteData): Observable<any> {
    return this.postDynamicAPI('suitePriceCategory/update', params);
  }

  /**
   * Request upsert guest on Travel studio
   *
   * @param {IReservationApiBookingVersionRequestParameters} params
   * @return {*}
   * @memberof ReservationBookingApiService
   */
  upsertGuestOnTS(params: IReservationApiTsUpserGuest) {
    const payload = {
      id: (params.id ?? '').toString(),
      DB: params.DB
    }
    return this.callDynamicAPI('oem/travelstudio/sendGuest2TS', payload, 'POST').pipe(
      map<any, any>(res => {
        return res;
      })
    );
  }
}
